import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { useEffect } from "react";
import { saveSeoMetadata } from "@/utils/seoUtils";
import { Compass, Calendar, CreditCard, Sun, Palmtree, Map, Umbrella, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import LoanTypeCard from "@/components/home/LoanTypeCard";

const seoKeywords = [
  "lomalaina", "matkalaina", "lainat lomamatkaan", 
  "lomarahoitus", "matkan rahoitus", "loma luotolla",
  "edullinen lomalaina", "lomalainan kilpailutus", 
  "lomalle lainalla", "matkaluotto",
  "lainaa lomalle", "laina ulkomaanmatkaan"
];

const LomalainaPage = () => {
  const seoTitle = "Lomalaina | Kilpailuta edullinen rahoitus lomamatkaan | Lainasampo";
  const seoDescription = "Kilpailuta lomalaina Lainasampo.fi kautta. Vertaa lomatarjouksia ja löydä edullisin rahoitus unelmiesi lomamatkaa varten nopeasti ja helposti.";
  const pageUrl = "https://lainasampo.fi/lainat/lomalaina";
  
  useEffect(() => {
    saveSeoMetadata("/lainat/lomalaina", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);
  
  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />
      
      <div className="relative overflow-hidden bg-cover bg-center h-[500px]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2073&auto=format&fit=crop')" }}>
        <div className="absolute inset-0 bg-blue-900/60"></div>
        <div className="container mx-auto px-4 relative z-10 h-full flex flex-col justify-center">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInVariants}
            className="max-w-2xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Toteuta unelmiesi lomamatka</h1>
            <p className="text-xl text-blue-50 mb-8">Älä anna rahan olla esteenä - kilpailuta lomalaina ja nauti ansaituista lomapäivistä nyt!</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/kilpailuta" className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-8 rounded-lg transition duration-300 text-center">
                Kilpailuta lomalaina
              </Link>
              <Link to="/artikkelit/hauska-lomalaina-rahoita-unelmalomasi-suomen-parhaisiin-luontokohteisiin" className="bg-white hover:bg-blue-50 text-blue-700 font-medium py-3 px-8 rounded-lg transition duration-300 text-center">
                Lue lomavinkit
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-blue-700 mb-2">Miksi valita lomalaina?</h2>
          <p className="text-gray-600 mb-10 max-w-3xl mx-auto">Lomalainan avulla voit toteuttaa unelmalomasi ilman pitkää säästämistä. Lainasampo auttaa löytämään edulliset lainaehdot lomabudjettiisi.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white border-blue-100 hover:shadow-md transition duration-300">
              <CardHeader className="pb-2">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg text-blue-700 text-center">Nopea rahoitus</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Saat rahoituksen lomallesi jopa samana päivänä hakemuksen jättämisestä.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-blue-100 hover:shadow-md transition duration-300">
              <CardHeader className="pb-2">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg text-blue-700 text-center">Joustavat ehdot</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Valitse itsellesi sopiva takaisinmaksuaika ja kuukausierä budjettisi mukaan.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-blue-100 hover:shadow-md transition duration-300">
              <CardHeader className="pb-2">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <Compass className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg text-blue-700 text-center">Vapaus valita</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Käytä laina haluamaasi kohteeseen - ulkomaille, kotimaan matkailuun tai lomamökkiin.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-blue-100 hover:shadow-md transition duration-300">
              <CardHeader className="pb-2">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <Award className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg text-blue-700 text-center">Kilpailutettu hinta</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600">Lainasampo.fi kilpailuttaa puolestasi parhaat tarjoukset, sinä vain valitset sopivimman.</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-blue-700 mb-2 text-center">Suosituimmat lomakohteet 2025</h2>
          <p className="text-gray-600 mb-10 text-center max-w-3xl mx-auto">Näihin kohteisiin suomalaiset matkustavat eniten - tutustu ja inspiroidu!</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="relative rounded-xl overflow-hidden group h-64 cursor-pointer">
              <img src="https://images.unsplash.com/photo-1516483638261-f4dbaf036963?q=80&w=2066&auto=format&fit=crop" alt="Italia" className="w-full h-full object-cover transition duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-5 text-white">
                <h3 className="text-xl font-bold">Italia</h3>
                <p className="flex items-center">
                  <Sun className="h-4 w-4 mr-1 text-yellow-300" />
                  Kulttuuri ja ruoka
                </p>
              </div>
            </div>
            
            <div className="relative rounded-xl overflow-hidden group h-64 cursor-pointer">
              <img src="https://images.unsplash.com/photo-1499678329028-101435549a4e?q=80&w=2070&auto=format&fit=crop" alt="Kreikka" className="w-full h-full object-cover transition duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-5 text-white">
                <h3 className="text-xl font-bold">Kreikka</h3>
                <p className="flex items-center">
                  <Umbrella className="h-4 w-4 mr-1 text-cyan-300" />
                  Saaret ja rannat
                </p>
              </div>
            </div>
            
            <div className="relative rounded-xl overflow-hidden group h-64 cursor-pointer">
              <img src="https://images.unsplash.com/photo-1530968033775-2c92736b131e?q=80&w=2070&auto=format&fit=crop" alt="Thaimaa" className="w-full h-full object-cover transition duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-5 text-white">
                <h3 className="text-xl font-bold">Thaimaa</h3>
                <p className="flex items-center">
                  <Palmtree className="h-4 w-4 mr-1 text-green-300" />
                  Eksotiikka ja edullisuus
                </p>
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-blue-700 mb-2 text-center">Näin haet lomalainaa</h2>
          <p className="text-gray-600 mb-10 text-center max-w-3xl mx-auto">Nopea ja helppo hakuprosessi mahdollistaa unelmalomasi toteuttamisen kätevästi.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold text-blue-700 mb-2">Täytä hakemus</h3>
              <p className="text-gray-600">Täytä lainahakemus verkossa vain muutamassa minuutissa - tarvitset vain henkilötiedot ja tulotiedot.</p>
            </div>
            
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold text-blue-700 mb-2">Vertaa tarjouksia</h3>
              <p className="text-gray-600">Saat useita lainatarjouksia eri pankeista ja rahoituslaitoksista - valitse niistä paras.</p>
            </div>
            
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold text-blue-700 mb-2">Nauti lomastasi</h3>
              <p className="text-gray-600">Rahat siirtyvät tilillesi nopeimmillaan saman päivän aikana - voit aloittaa loman suunnittelun heti!</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-blue-700 mb-2 text-center">Millainen lomamatka on sinun suunnitelmissasi?</h2>
          <p className="text-gray-600 mb-10 text-center max-w-3xl mx-auto">Lomalaina sopii monenlaisiin lomamatkaideoihin.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-100 hover:shadow-lg transition duration-300">
              <CardHeader>
                <Sun className="h-10 w-10 text-yellow-500 mx-auto" />
                <CardTitle className="text-center text-blue-700">Rantalomat</CardTitle>
              </CardHeader>
              <CardContent className="text-center text-gray-600">
                <p>Aurinko, hiekka ja meri - täydellinen irtiotto arjesta lämpimässä rantakohteessa.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-100 hover:shadow-lg transition duration-300">
              <CardHeader>
                <Map className="h-10 w-10 text-blue-500 mx-auto" />
                <CardTitle className="text-center text-blue-700">Kaupunkilomat</CardTitle>
              </CardHeader>
              <CardContent className="text-center text-gray-600">
                <p>Kulttuuria, historiaa ja ostoksia - eurooppalaiset kaupungit kutsuvat löytöretkelle.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-100 hover:shadow-lg transition duration-300">
              <CardHeader>
                <Palmtree className="h-10 w-10 text-green-500 mx-auto" />
                <CardTitle className="text-center text-blue-700">Kaukokohteet</CardTitle>
              </CardHeader>
              <CardContent className="text-center text-gray-600">
                <p>Eksoottisia elämyksiä Aasiassa tai Väli-Amerikassa - kokemuksia jotka muistat aina.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-100 hover:shadow-lg transition duration-300">
              <CardHeader>
                <Compass className="h-10 w-10 text-red-500 mx-auto" />
                <CardTitle className="text-center text-blue-700">Aktiivilomat</CardTitle>
              </CardHeader>
              <CardContent className="text-center text-gray-600">
                <p>Sukellusta, vaellusta tai laskettelua - täydelliset puitteet harrastuksille.</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between"
        >
          <div className="mb-6 md:mb-0 md:mr-8">
            <h3 className="text-2xl font-bold mb-2">Toteuta unelmalomasi tänään!</h3>
            <p className="text-blue-100">Kilpailuta lomalaina ja valitse itsellesi paras tarjous. Kaikki suurimmat pankit ja rahoituslaitokset yhdellä hakemuksella.</p>
          </div>
          <Link 
            to="/kilpailuta" 
            className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 rounded-xl font-medium transition-colors whitespace-nowrap text-center"
          >
            Hae lomalainaa
          </Link>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16"
        >
          <h2 className="text-3xl font-bold text-blue-700 mb-8 text-center">Lue lisää lomaideoista</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <LoanTypeCard
              title="Lomavinkit 2025"
              description="Suosituimmat kohteet ja säästövinkit"
              detailText="Lue suosituimmista lomakohteista vuodelle 2025 ja vinkkejä kuinka rahoittaa unelmalomasi edullisesti."
              icon={Map}
              linkPath="/artikkelit/hauska-lomalaina-rahoita-unelmalomasi-suomen-parhaisiin-luontokohteisiin"
              imagePath="https://images.unsplash.com/photo-1472396961693-142e6e269027?q=80&w=1800&auto=format&fit=crop"
            />
            
            <LoanTypeCard
              title="Venematkailu"
              description="Unelmien veneloma"
              detailText="Tutki vaihtoehtoja veneellä matkustamiseen. Miten rahoittaa oma vene tai venematka järkevimmällä tavalla?"
              icon={Umbrella}
              linkPath="/artikkelit/venelainaopas-nain-rahoitat-unelmiesi-veneen"
              imagePath="https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?q=80&w=1800&auto=format&fit=crop"
            />
            
            <LoanTypeCard
              title="Lainaopas 2025"
              description="Kaikki lainaamisesta"
              detailText="Lue kuinka tehdä fiksuja lainapäätöksiä ja kilpailuttaa tarjoukset tehokkaasti. Kattava opas myös loman rahoittamiseen."
              icon={CreditCard}
              linkPath="/artikkelit/lainan-ottamisen-opas-2025"
              imagePath="https://images.unsplash.com/photo-1500375592092-40eb2168fd21?q=80&w=1800&auto=format&fit=crop"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LomalainaPage;
